import React, { useRef, useState } from 'react';

import { Add, Web } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { Form, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Breadcrumbs,
  Button,
  CheckboxRow,
  FormDateField,
  FormTextField,
  InfoTextField,
  SubHeader,
} from '../../../../../components';
import { searchExpenseById, saveManualExpense, uploadDocumentToExpense, getErrorMessage } from '../operations';

import './styles.scoped.css';

const ManualExpense = () => {
  const navigate = useNavigate();
  const userId = useSelector(state => state.admin.user.id);
  const fileInputRef = useRef(null);

  const [expenseId, setExpenseId] = useState('');
  const [expense, setExpense] = useState(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [searchError, setSearchError] = useState('');
  const [uploadError, setUploadError] = useState('');

  const [approvedFlag, setApprovedFlag] = useState(false);
  const [statusTransmitted, setStatusTransmitted] = useState(false);
  const [payrollWeekEndDate, setPayrollWeekEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [documents, setDocuments] = useState([]);

  // Per-field validation errors, surfaced under each field via its own
  // error / errorMessage props — not aggregated into one message at the bottom.
  const [fieldErrors, setFieldErrors] = useState({});
  const clearFieldError = key => setFieldErrors(prev => (prev[key] ? { ...prev, [key]: undefined } : prev));

  const handleSearch = async () => {
    if (!expenseId.trim()) {
      setSearchError('Please enter an Expense ID.');
      return;
    }
    setSearching(true);
    setSearchError('');
    setExpense(null);
    try {
      const result = await searchExpenseById(expenseId.trim());
      if (result) {
        setExpense(result);
        setNotes(result.notes || '');
      } else {
        setSearchError('Expense not found.');
      }
    } catch {
      setSearchError('Error searching for expense.');
    } finally {
      setSearching(false);
    }
  };

  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError('');
    try {
      const doc = await uploadDocumentToExpense({ expenseId, file, userId });
      if (doc) setDocuments(prev => [...prev, doc]);
    } catch (err) {
      setUploadError(getErrorMessage(err, 'Upload failed. Please try again.'));
    }
    e.target.value = '';
  };

  const handleSave = async () => {
    // Save is always enabled; if no expense has been loaded yet, prompt for the
    // Expense ID inline (under that field) rather than blocking via a disabled button.
    if (!expense) {
      setSearchError('Please enter an Expense ID.');
      return;
    }
    const nextErrors = {};
    if (!approvedFlag) nextErrors.approvedFlag = 'Required before transmitting.';
    if (!statusTransmitted) nextErrors.statusTransmitted = 'Required before transmitting.';
    if (!payrollWeekEndDate) nextErrors.payrollWeekEndDate = 'Payroll Week Ending Date is required.';
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setSaving(true);
    setSaveError('');
    try {
      await saveManualExpense({
        expenseId,
        userId,
        approvedFlag,
        statusTransmitted,
        payrollWeekEndDate,
        notes,
      });
      navigate('/expense-entry/expense-maintenance');
    } catch (e) {
      setSaveError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SubHeader
        text="Expenses"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Expenses?"
      />
      <Breadcrumbs
        leftTitle="Expense Maintenance"
        rightTitle="Manual Expense"
        route="/expense-entry/expense-maintenance"
      />
      <div className="wrap">
        <div className="maintenance-card">
          <div className="card-tab">Set Manual Expense to Transmitted</div>
          <div className="card-content">
            <div className="search-row">
              <FormTextField
                label="Enter Expense ID"
                value={expenseId}
                onChange={e => {
                  setExpenseId(e.target.value);
                  setSearchError('');
                }}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                error={!!searchError}
                errorMessage={searchError}
                disabled={searching}
              />
              {searching ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Button variant="dark" size="lg" onPress={handleSearch} text="Search" />
              )}
            </div>

            {expense && (
              <>
                <div className="expense-details-grid">
                  <InfoTextField
                    label="Driver Name & Driver Number"
                    required
                    value={`${expense.driverName} - ${expense.driverId}`}
                  />
                  <InfoTextField label="Total To Be Billed" value={expense.totalToBeBilled} />
                  <InfoTextField label="Code" value={expense.code} />
                  <InfoTextField label="Status" value={expense.status} />
                </div>
                <InfoTextField
                  label="Customer Name & Customer Number"
                  required
                  value={`${expense.customerName} - ${expense.customerId}`}
                />

                <div className="inner-section">
                  <CheckboxRow
                    label='Set flag on "Approved to Transmit"'
                    checked={approvedFlag}
                    onChange={e => {
                      setApprovedFlag(e.target.checked);
                      clearFieldError('approvedFlag');
                    }}
                    error={!!fieldErrors.approvedFlag}
                    errorMessage={fieldErrors.approvedFlag}
                  />
                  <CheckboxRow
                    label="Change Status to Transmitted"
                    checked={statusTransmitted}
                    onChange={e => {
                      setStatusTransmitted(e.target.checked);
                      clearFieldError('statusTransmitted');
                    }}
                    error={!!fieldErrors.statusTransmitted}
                    errorMessage={fieldErrors.statusTransmitted}
                  />
                  <FormDateField
                    label="Enter Payroll Week Ending Date"
                    required
                    value={payrollWeekEndDate}
                    onChange={e => {
                      setPayrollWeekEndDate(e.target.value);
                      clearFieldError('payrollWeekEndDate');
                    }}
                    error={!!fieldErrors.payrollWeekEndDate}
                    errorMessage={fieldErrors.payrollWeekEndDate}
                  />
                  <div className="attach-row">
                    <span className="attach-label">Attach Receipt</span>
                    <IconButton
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        backgroundColor: '#da0f2e',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#a50d25' },
                        padding: '6px',
                      }}
                    >
                      <Add />
                    </IconButton>
                    <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />
                  </div>
                  {uploadError && <p className="error-text">{uploadError}</p>}
                  <table className="documents-table">
                    <thead>
                      <tr>
                        <th>Document Name</th>
                        <th>Upload By</th>
                        <th>Uploaded Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((doc, i) => (
                        <tr key={i}>
                          <td>{doc.fileName}</td>
                          <td>{doc.uploadedBy}</td>
                          <td>{doc.uploadedDate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Form.Group>
                    <Form.Label>Notes</Form.Label>
                    <Form.Control as="textarea" rows={4} value={notes} onChange={e => setNotes(e.target.value)} />
                  </Form.Group>
                </div>
              </>
            )}
          </div>
          {/* Form-wide save/server error only — per-field validation shows under each field. */}
          {saveError && <p className="error-text save-error">{saveError}</p>}
          <div className="buttons-container">
            <Button variant="primary" size="lg" onPress={handleSave} text="Save" disabled={saving} />
            <Button
              variant="outline"
              size="lg"
              onPress={() => navigate('/expense-entry/expense-maintenance')}
              text="Cancel"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ManualExpense;
